import { useState } from 'react';
import { Music } from 'lucide-react';
import { sendDjSignup, emailOk } from '@/lib/klaviyo';
import { Field, inputCls, Status } from './forms';
import { Reveal } from './Reveal';

export default function DjSignup() {
  const [msg, setMsg] = useState({ kind: 'idle', text: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const trap = form.querySelector('[name=website]');
    if (trap && trap.value) return; // bot trap
    setMsg({ kind: 'idle', text: '' });
    const name = form.djName.value.trim();
    if (name.length < 2) {
      setMsg({ kind: 'err', text: 'Tell us your DJ or artist name.' });
      return;
    }
    if (!emailOk(form.email.value.trim())) {
      setMsg({ kind: 'err', text: 'Pop in a valid email address.' });
      return;
    }
    const digits = form.phone.value.replace(/\D/g, '');
    if (digits.length < 10) {
      setMsg({ kind: 'err', text: 'Pop in a valid mobile number.' });
      return;
    }
    const btn = form.querySelector('[type=submit]');
    const label = btn.textContent;
    btn.disabled = true;
    sendDjSignup({ djName: name, email: form.email.value.trim(), phone: form.phone.value })
      .then(() => {
        form.reset();
        setMsg({ kind: 'ok', text: 'Nearly there — check your inbox (and spam) and hit confirm so the music lands with you.' });
      })
      .catch(() => setMsg({ kind: 'err', text: "That didn't go through. Please try again in a moment." }))
      .finally(() => {
        btn.disabled = false;
        btn.textContent = label;
      });
  };

  return (
    <section id="djs" className="mx-auto max-w-[1280px] scroll-mt-24 px-6 pt-24 md:pt-32" data-testid="dj-section">
      <Reveal>
        <div className="relative overflow-hidden rounded-[32px] border border-sky/30 bg-sky/10 p-8 md:p-14">
          <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-sky/20 blur-3xl" aria-hidden="true" />
          <div className="relative grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <div className="flex flex-col items-start gap-5">
              <span className="flex items-center gap-2 font-mono text-[13px] font-bold uppercase tracking-[0.25em] text-sky">
                <Music size={15} aria-hidden="true" />
                For DJs only
              </span>
              <h2 className="font-display text-5xl uppercase leading-[0.95] text-ivory md:text-6xl">
                GET THE MUSIC <span className="text-sky">FIRST</span>
              </h2>
              <p className="max-w-[460px] text-lg text-ivory/70">
                Princess Nyah sends her tracks, edits and drops straight to DJs before release.
                Sign up and play them first.
              </p>
            </div>

            <form className="flex flex-col gap-4" noValidate onSubmit={handleSubmit} data-testid="dj-form">
              <Field label="DJ / artist name" htmlFor="dj-name">
                <input id="dj-name" name="djName" type="text" placeholder="What do you go by?" required className={inputCls} data-testid="dj-name-input" />
              </Field>
              <Field label="Email" htmlFor="dj-email">
                <input id="dj-email" name="email" type="email" autoComplete="email" placeholder="you@email.com" required className={inputCls} data-testid="dj-email-input" />
              </Field>
              <Field label="Mobile" htmlFor="dj-phone">
                <input id="dj-phone" name="phone" type="tel" autoComplete="tel" placeholder="07…" required className={inputCls} data-testid="dj-phone-input" />
              </Field>
              <div className="absolute left-[-9999px] h-px w-px overflow-hidden" aria-hidden="true">
                <label htmlFor="dj-web">Leave this empty</label>
                <input id="dj-web" name="website" tabIndex={-1} autoComplete="off" />
              </div>
              <button
                type="submit"
                data-testid="dj-submit"
                className="mt-2 rounded-full bg-sky px-7 py-4 font-extrabold text-plum transition-transform duration-200 hover:scale-[1.03] hover:shadow-[0_0_36px_rgba(142,197,220,0.5)] disabled:opacity-60"
              >
                Send me the music
              </button>
              <Status msg={msg} />
              <p className="text-[13px] text-ivory/45">Tracks, edits &amp; drops before release. DJs only.</p>
            </form>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
